import { useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';
import React, { SetStateAction, useMemo } from 'react';

import { AutocompleteField, MultiValueAutocompleteField } from '@components/FormFields';
import { autocompleteEntities } from '@utils/backend-api/entities';
import {
  FilterOption as FilterOptionType,
  FilterOptionsResponse,
} from '@utils/backend-api/subscriber_directory';

import { INITIAL_FORM_VALUES, ShowFilterMap } from './Layout';

interface FilterOptionProps {
  fieldName: string;
  filterOptions?: FilterOptionType[];
  icon: string;
  input: React.ReactNode;
  label: string;
  optionName?: string;
  searchOptions?: {
    isMultiValueField?: boolean;
    searchParams: {
      order_entities_by?: string[];
      relationship_sources: string[];
      relationship_type?: string;
    };
  };
  setDisplayedFilterOptions?: (option: SetStateAction<FilterOptionsResponse>) => void;
  setShowFilterMap: (showFilterMap: SetStateAction<ShowFilterMap>) => void;
  showFilter: boolean;
}

const FilterOption = ({
  fieldName,
  filterOptions,
  icon,
  input,
  label,
  optionName,
  searchOptions,
  setDisplayedFilterOptions,
  setShowFilterMap,
  showFilter,
}: FilterOptionProps) => {
  const { setFieldValue, values } = useFormikContext();

  const isOriginalValue = useMemo(
    () => isEqual(values[fieldName], INITIAL_FORM_VALUES[fieldName]),
    [values, fieldName]
  );

  const handleToggleFilter = () => {
    setShowFilterMap((prevState: ShowFilterMap) => ({
      ...prevState,
      [fieldName]: !prevState[fieldName],
    }));
  };

  const searchOnChange = (value) => {
    const isMultiField = Array.isArray(value);

    let addedVal;
    let addedValLowerCase;
    if (isMultiField) {
      addedVal = value.slice(-1)[0]?.label;
      addedValLowerCase = addedVal?.toLowerCase();
      const updatedVal = [...value.slice(0, -1), addedValLowerCase];
      setFieldValue(fieldName, updatedVal);
    } else {
      addedVal = value?.label;
      addedValLowerCase = addedVal?.toLowerCase();

      setFieldValue(fieldName, addedValLowerCase);
    }
    const updatedOption = { label: addedVal, value: addedValLowerCase };
    if (!filterOptions.map((o) => o.value).includes(addedValLowerCase)) {
      setDisplayedFilterOptions((prevState) => ({
        ...prevState,
        [optionName]: [...prevState[optionName], updatedOption],
      }));
    }
  };

  const SearchField = searchOptions?.isMultiValueField
    ? MultiValueAutocompleteField
    : AutocompleteField;

  return (
    <div className={`FilterOption ${fieldName}-filter`}>
      <div className="filter-container">
        <div className="filter-header">
          <a className="toggle-filter-collapse" onClick={handleToggleFilter}>
            <div className="category-container">
              <img src={icon} />
              <div className="category-name">{label}</div>
            </div>
          </a>
          <div className="actions">
            {!isOriginalValue && (
              <a
                className="clear-btn"
                onClick={() => setFieldValue(fieldName, INITIAL_FORM_VALUES[fieldName])}
              >
                Clear
              </a>
            )}
            <a className="toggle-filter-collapse" onClick={handleToggleFilter}>
              {showFilter ? (
                <i className="fa fa-chevron-up" />
              ) : (
                <i className="fa fa-chevron-down" />
              )}
            </a>
          </div>
        </div>
        <div className={`filter-input-wrapper ${showFilter ? '' : 'hide'}`}>
          {searchOptions && (
            <div>
              <SearchField
                placeholder={`Search ${fieldName}`}
                options={filterOptions}
                name={fieldName}
                showPills={false}
                isCreatable={false}
                isClearable={false}
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                    ...searchOptions.searchParams,
                  })
                }
                onChange={searchOnChange}
              />
            </div>
          )}
          <div>{input}</div>
        </div>
      </div>
    </div>
  );
};

export default FilterOption;
